import { ReactNode, SyntheticEvent, useCallback } from 'react';
import { PAGINATION_ELLIPSIS, usePaginationItems } from './usePaginationItems';

interface IUsePaginatorControlsParams {
  onPageChange: (page: number) => void;
  currentPage: number;
  numPages: number;
  pageButtonLimit: number;
  nextPageLabel?: string;
  previousPageLabel?: string;
}

interface IPaginatorControls {
  previous: ReactNode;
  pages: ReactNode;
  next: ReactNode;
}

const pageHref = (num: number) => `#page-${num + 1}`;

export const usePaginatorControls = ({
  onPageChange,
  currentPage,
  numPages,
  pageButtonLimit,
  nextPageLabel,
  previousPageLabel
}: IUsePaginatorControlsParams): IPaginatorControls => {
  const pageItems = usePaginationItems({ currentPage, numPages, pageButtonLimit });

  const handlePrevious = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      onPageChange(currentPage - 1);
    },
    [currentPage, onPageChange]
  );

  const handleNext = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      onPageChange(currentPage + 1);
    },
    [currentPage, onPageChange]
  );

  const handlePageButton = useCallback(
    (page: number, e: SyntheticEvent) => {
      e.preventDefault();
      onPageChange(page);
    },
    [onPageChange]
  );

  const renderPageButton = useCallback(
    (pageNum: number) => {
      let className = 'reactable-page-button';

      if (currentPage === pageNum) {
        className += ' reactable-current-page';
      }

      return (
        <a
          className={className}
          key={pageNum}
          href={pageHref(pageNum)}
          onClick={(e) => handlePageButton(pageNum, e)}
          data-cy={`pagination-page-${pageNum + 1}`}
        >
          {pageNum + 1}
        </a>
      );
    },
    [currentPage, handlePageButton]
  );

  const previous =
    currentPage > 0 ? (
      <a
        className="reactable-previous-page"
        href={pageHref(currentPage - 1)}
        onClick={handlePrevious}
        data-cy="pagination-prev"
      >
        {previousPageLabel || 'Previous'}
      </a>
    ) : null;

  const next =
    currentPage < numPages - 1 ? (
      <a
        className="reactable-next-page"
        href={pageHref(currentPage + 1)}
        onClick={handleNext}
        data-cy="pagination-next"
      >
        {nextPageLabel || 'Next'}
      </a>
    ) : null;

  const pages = pageItems.map((item, index) =>
    item === PAGINATION_ELLIPSIS ? (
      <span
        className="reactable-pagination-ellipsis"
        // eslint-disable-next-line react/no-array-index-key
        key={`ellipsis-${index}`}
      >
        &hellip;
      </span>
    ) : (
      renderPageButton(item)
    )
  );

  return { previous, pages, next };
};
